import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useQueryClient } from 'react-query';
import dayjs from 'dayjs';

// forms
import { LiveChatForm } from './forms/LiveChatForm';
// context
import { ISOCKET_ACTIONS } from 'context/socket/types';
import { useSockets } from 'context/socket/SocketProvider';
// styles
import { Gap } from './layout';
// types
import { User } from 'types';
// utils
import { URL_REGEX } from 'utils/regexes';
import { checkIfAdvisorIsOnline } from 'utils/date';
// queries
import { useConversationsQuery } from 'queries/conversations/conversationsQuery';
// keys
import { conversationsQueryKeys } from 'queries/conversations/conversationsQueryKeys';

enum MessageType {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

interface IMessageType {
  type: 'inbound' | 'outbound';
}

const ChatAreaWrapper = styled.div<{ minimize: boolean }>`
  margin: 0 -24px;

  background: #f2f4f6;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  height: calc(100% - 64px - 72px);
  ${({ minimize }) =>
    minimize &&
    css`
      display: none;
    `}
`;

const EmptyMessage = styled.p`
  color: rgba(0, 27, 83, 0.6);
  margin: 0;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  text-align: center;
`;

const SingleMessageWrapper = styled(Gap)``;

const SingleMessage = styled.div<IMessageType>`
  padding: 12px 16px;
  gap: 10px;

  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;

  ${({ type, theme }) =>
    type === MessageType.INBOUND &&
    css`
      color: white;
      background: ${theme.colors.primary[100]};
      border-radius: 8px 8px 0px 8px;
    `}
  ${({ type, theme }) =>
    type === MessageType.OUTBOUND &&
    css`
      color: ${theme.colors.primary[100]};
      background: white;
      border-radius: 8px 8px 8px 0px;
    `}
`;

const AdditionalInfo = styled.span<IMessageType>`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 27, 83, 0.6);
  ${({ type }) =>
    type === MessageType.OUTBOUND &&
    css`
      text-align: right;
    `}
`;

const ScrollableView = styled(Gap)`
  overflow: scroll;
  width: calc(100% - 48px);
  padding: 8px 24px;
  height: calc(100% - 16px);
`;

const Availabilty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background: white;
  border-radius: 5px;
  text-align: center;
`;

const AvailabiltyParagraph = styled.p`
  margin: 0;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  color: rgba(0, 27, 83, 0.8);
`;

const detectUrlAndText = (txt: string) => {
  return (
    txt?.split(' ')?.map((part: string, i: number) =>
      URL_REGEX.test(part) ? (
        <a
          key={`links-${i}`}
          target='_blank'
          href={part}
          rel='noreferrer'
          style={{ color: 'inherit' }}
        >
          {part}{' '}
        </a>
      ) : (
        part + ' '
      )
    ) || 'Something went wrong with parsing your message'
  );
};

interface ILiveChat {
  advisorDetails: User;
  userId: number;
  minimize: boolean;
  setUnread: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LiveChat = ({
  advisorDetails,
  userId,
  minimize,
  setUnread,
}: ILiveChat) => {
  const { socket, currentConversation } = useSockets();
  const conversationId = currentConversation?.conversationId;
  const queryClient = useQueryClient();

  const { data, isError, isLoading } = useConversationsQuery(conversationId);
  const isOnline = checkIfAdvisorIsOnline(advisorDetails);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current === null) {
      return;
    }
    messagesEndRef?.current.scrollIntoView(false);
  };

  // Socket use effect fot connect conversations
  React.useEffect(() => {
    if (socket === null || !conversationId) {
      return;
    }

    socket?.on(ISOCKET_ACTIONS.MESSAGE_RECEIVED, () => {
      queryClient.invalidateQueries(conversationsQueryKeys.all());
    });
  }, [socket]);

  // scrollToBottom
  useEffect(scrollToBottom, [messagesEndRef?.current, data, isLoading]);

  if (isLoading) {
    return (
      <>
        <ChatAreaWrapper
          style={{ height: 'calc(100% - 65px)' }}
          minimize={minimize}
        >
          <EmptyMessage>Loading....</EmptyMessage>
        </ChatAreaWrapper>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <ChatAreaWrapper
          style={{ height: 'calc(100% - 65px)' }}
          minimize={minimize}
        >
          <EmptyMessage>
            Something went wrong loading your messages!
          </EmptyMessage>
        </ChatAreaWrapper>
      </>
    );
  }

  if (!data?.messages?.length) {
    return (
      <>
        <ChatAreaWrapper minimize={minimize}>
          <EmptyMessage>
            {advisorDetails.firstName} is {isOnline ? '' : 'not'} available
            right now.
            <br /> Leave a message and {advisorDetails.firstName} will get back
            to you.
          </EmptyMessage>
        </ChatAreaWrapper>
        {!minimize && (
          <LiveChatForm
            userId={userId}
            scrollToBottom={scrollToBottom}
            setUnread={setUnread}
          />
        )}
      </>
    );
  }

  return (
    <>
      <ChatAreaWrapper minimize={minimize}>
        <ScrollableView direction='column' gap='12px'>
          {isOnline && (
            <Availabilty>
              <AvailabiltyParagraph>
                You started the live chat.
              </AvailabiltyParagraph>
            </Availabilty>
          )}
          {data?.messages.map(message => (
            <SingleMessageWrapper
              key={message.messageId}
              direction='column'
              gap='8px'
            >
              <SingleMessage type={message.type || MessageType.OUTBOUND}>
                {detectUrlAndText(message.text)}
              </SingleMessage>
              <AdditionalInfo type={message.type || MessageType.OUTBOUND}>
                {message.type === MessageType.INBOUND
                  ? data?.contact?.email || data?.contact?.phone
                  : message?.user?.firstName}
                · {dayjs(message.createdAt).format('DD MMMM, hh:mm a')}
              </AdditionalInfo>
            </SingleMessageWrapper>
          ))}
          {!isOnline && (
            <Gap direction='column'>
              <Availabilty>
                <AvailabiltyParagraph>
                  {advisorDetails.firstName} {advisorDetails.lastName} will get
                  back to you via email or phone.
                </AvailabiltyParagraph>
              </Availabilty>
              <Availabilty>
                <AvailabiltyParagraph>
                  Feel free to close the chat window if you don’t have more
                  questions.
                </AvailabiltyParagraph>
              </Availabilty>
            </Gap>
          )}
          <div ref={messagesEndRef} />
        </ScrollableView>
      </ChatAreaWrapper>
      {!minimize && (
        <LiveChatForm
          userId={userId}
          scrollToBottom={scrollToBottom}
          setUnread={setUnread}
        />
      )}
    </>
  );
};
